import React from 'react'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import { decodeQueryParams } from 'use-query-params'
import {
  removeEmptyFieldObject,
  SERVICES_URL,
  theme,
  useTranslation
} from '@plandi/common'

import {
  MainLayout,
  MainLayoutContent,
  MainLayoutSidebar
} from 'src/features/layouts'
import {
  ProjectListUser,
  ProjectListUserFiltersQueryParamConfigMap,
  useProjectListUserFilters
} from 'src/features/projects'
import {
  projectsApi,
  projectKeys,
  useQueryProjectsByProfileId
} from 'src/features/projects/common'
import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
import { useMediaQuery } from '@material-ui/core'
import { sharedApi, TRANSLATE } from '../../../shared'
import { ProfileDetails } from 'src/features/profile-details'

export const getServerSideProps = async ({
  query,
  locale
}: GetServerSidePropsContext) => {
  const queryClient = new QueryClient()

  const { userId, ...params } = removeEmptyFieldObject(
    decodeQueryParams(ProjectListUserFiltersQueryParamConfigMap, query)
  )

  if (userId) {
    await queryClient.prefetchQuery(
      [
        projectKeys.user(userId as number, params as Record<string, unknown>),
        locale
      ],
      () => projectsApi.getProjectsByUserId(userId, locale ?? 'en', params)
    )
  }
  await queryClient.prefetchQuery(TRANSLATE, () =>
    sharedApi.getTranslate(locale)
  )

  return {
    props: {
      locale: locale,
      dehydratedState: dehydrate(queryClient)
    }
  }
}

const ProjectsProfilePage = (props: { locale: string }) => {
  const router = useRouter()
  const userId = router.query.userId ? Number(router.query.userId) : undefined
  const isDesktop = useMediaQuery(theme.breakpoints.up(960))
  const { sort } = useProjectListUserFilters()
  const { data: translate } = useTranslation()
  const url = SERVICES_URL
  const locale = props.locale

  const type = router.query.type as string

  const { data: projectsUser, isLoading } = useQueryProjectsByProfileId(
    type,
    userId,
    {
      sort
    }
  )

  return (
    <MainLayout withFooter title={translate?.shop['user-projects'] ?? ''}>
      <div>
        <BreadCrumbs
          breadcrumbInfo={[
            {
              title: translate?.market.title ?? '',
              href: locale === 'ru' ? `${url.shop}/ru` : url.shop
            },
            {
              title: translate?.projects ?? '',
              href:
                locale === 'ru'
                  ? `${url.shop}/ru/projects`
                  : `${url.shop}/projects`
            },
            { title: `${projectsUser?.contactData?.fullName}`, href: '' }
          ]}
        />
        <div style={isDesktop ? { display: 'flex' } : {}}>
          {(projectsUser?.contactData || isLoading) && (
            <MainLayoutSidebar size="large">
              <ProfileDetails
                userPage
                projectsProfile={projectsUser?.profile}
                loadingProfile={isLoading}
              />
            </MainLayoutSidebar>
          )}
          <MainLayoutContent>
            <ProjectListUser
              contact
              isLoading={isLoading}
              projects={projectsUser?.projects}
              contactData={projectsUser?.contactData}
              translate={translate}
              locale={props.locale}
            />
          </MainLayoutContent>
        </div>
      </div>
    </MainLayout>
  )
}

export default ProjectsProfilePage
